import React, { useMemo } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { Platform, StyleSheet, TouchableOpacity } from './rn';
import { useThemeMode } from '../theme/ThemeContext';
import { getGlassView, nativeGlassAvailable } from './nativeGlass';

type Props = {
  onPress: () => void;
  accessibilityLabel: string;
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

/**
 * Cam kontrol düğmesi — uygulamadaki tek kaynağı (7.14 / D-109).
 *
 * D-109 camı yalnız kontrol ve navigasyon katmanında, önemli eylemlerde, sembol
 * başına ayrı 40×40 yuvarlakta kullanıyor. Yüzey bilinçli olarak bir React Native
 * yaklaşımıdır — yarı saydam dolgu, hairline çerçeve, üst kenar parlaması, yumuşak
 * gölge. Apple'ın native Liquid Glass malzemesi DEĞİLDİR; `expo-glass-effect` bir
 * kez eklenip geri alındı ve yeni bir açık onay olmadan geri gelmez.
 *
 * Boya yalnız iOS'ta: Android ve web aynı işlevsel başlığı camsız alır.
 *
 * Neden bileşen: D-506 ölçümünde bu stiller `MessageScreen` ve `HeaderBackButton`
 * içinde **iki kez** kopyalanmış hâlde bulundu — o gün birebir aynıydılar, ama
 * ortak kaynağı olmayan iki kopyanın aynı kalması şansa bağlıdır. Yeni bir cam
 * kontrol bu bileşenle yapılır; üçüncü bir kopya açılmaz.
 */
export default function GlassControlButton({
  onPress, accessibilityLabel, children, style, testID,
}: Props) {
  const { isDark } = useThemeMode();
  const styles = useMemo(() => makeStyles(isDark), [isDark]);
  const yerel = useMemo(() => nativeGlassAvailable(), []);
  const GlassView = useMemo(() => getGlassView(), []);

  return (
    <TouchableOpacity
      testID={testID}
      style={[yerel ? styles.nativeButton : styles.button, style]}
      onPress={onPress}
      activeOpacity={0.65}
      accessible
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}>
      {GlassView ? (
        /* Yerel malzeme kendi dolgusunu, kenarını ve gölgesini taşır; bizim
           yaklaşımımızın hiçbir parçası üstüne binmiyor. `colorScheme` sisteme
           değil uygulamanın kendi temasına bağlanıyor, çünkü tema uygulama
           içinden de değiştirilebiliyor. */
        <GlassView
          style={styles.nativeSurface}
          glassEffectStyle="regular"
          isInteractive
          colorScheme={isDark ? 'dark' : 'light'}
          pointerEvents="none"
        />
      ) : null}
      {children}
    </TouchableOpacity>
  );
}

const makeStyles = (isDark: boolean) => StyleSheet.create({
  /** Yerel malzemede yalnız ölçü ve yerleşim bizden; boya tamamen sistemin. */
  nativeButton: {
    width: 40,
    height: 40,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nativeSurface: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 20,
  },
  button: {
    width: 40,
    height: 40,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 20,
    overflow: 'visible',
    backgroundColor: Platform.OS === 'ios'
      ? (isDark ? 'rgba(44,44,46,0.78)' : 'rgba(246,246,248,0.78)')
      : 'transparent',
    borderWidth: Platform.OS === 'ios' ? StyleSheet.hairlineWidth : 0,
    borderColor: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.96)',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 5 },
    shadowOpacity: Platform.OS === 'ios' ? (isDark ? 0.34 : 0.13) : 0,
    shadowRadius: 12,
  },
});
